import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Close, LinkObject, Add } from "@dreamhub/designsystem/icons";
import { AddLinkedField } from "./AddLinkedField";
import { AddCustomField } from "./AddCustomField";
import { AddPresetFieldEdit } from "./AddPresetFieldEdit";
import { ActionCard } from "./components/ActionCard";
import { DrawerTitle as CustomDrawerTitle } from "./components/DrawerTitle";
import { IconButton } from "@/components/ui/icon-button";
import { AddPresetField } from "@/pages/settings/components/dealsSettings/FieldManagement/AddPresetField";
import { useFieldManagement } from "./hooks";
import { APIFieldItem } from "@/pages/settings/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";

const TRANSLATION_KEY = "settings.deals.fieldManagement.drawer";

type ViewState = "initial" | "linkedField" | "customField" | "presetField";

interface AddFieldDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (fieldData?: any) => void;
  entityType?: EntityType;
}

export const AddFieldDrawer: React.FC<AddFieldDrawerProps> = ({
  isOpen,
  onOpenChange,
  onSave,
  entityType = EntityType.DEAL
}) => {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState<ViewState>("initial");
  const [selectedPresetField, setSelectedPresetField] =
    useState<APIFieldItem | null>(null);
  const { scoFields } = useFieldManagement();

  React.useEffect(() => {
    if (isOpen) {
      if (
        entityType === EntityType.COMPANY ||
        entityType === EntityType.PARTNER ||
        entityType === EntityType.PARTNER_CONTACT
      ) {
        setCurrentView("customField");
      } else {
        setCurrentView("initial");
      }
      setSelectedPresetField(null);
    }
  }, [isOpen, entityType]);

  const handleClose = () => {
    setCurrentView("initial");
    setSelectedPresetField(null);
    onOpenChange(false);
  };

  const handleLinkExistingField = () => {
    setCurrentView("linkedField");
  };

  const handleStartFromScratch = () => {
    setCurrentView("customField");
  };

  const handlePresetFieldEdit = (field: APIFieldItem) => {
    setSelectedPresetField(field);
    setCurrentView("presetField");
  };

  const handleBack = () => {
    setCurrentView("initial");
    setSelectedPresetField(null);
  };

  const handleSave = (fieldData?: any) => {
    onSave?.(fieldData);
    handleClose();
  };

  const getTitle = () => {
    switch (currentView) {
      case "linkedField":
        return (
          <CustomDrawerTitle
            title={t(`${TRANSLATION_KEY}.title`)}
            subtitle={t(`${TRANSLATION_KEY}.linkExistingField`)}
            subtitleIcon={<LinkObject />}
          />
        );
      case "customField":
        return (
          <CustomDrawerTitle
            title={t(`${TRANSLATION_KEY}.title`)}
            subtitle={t(`${TRANSLATION_KEY}.customField`)}
          />
        );
      case "presetField":
        return (
          <CustomDrawerTitle
            title={t(`${TRANSLATION_KEY}.title`)}
            subtitle={selectedPresetField?.label || ""}
          />
        );
      default:
        return <CustomDrawerTitle title={t(`${TRANSLATION_KEY}.title`)} />;
    }
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={onOpenChange}
      direction="right"
      modal={false}
      dismissible={false}
    >
      <AppDrawerContent isSettingsDrawer>
        <DrawerHeader>
          <DrawerTitle>
            {getTitle()}
          </DrawerTitle>
          <IconButton
            data-test="close-field-drawer"
            onClick={handleClose}
            variant="iconNoBg"
            size="md"
          >
            <Close />
          </IconButton>
        </DrawerHeader>
        <div className="flex w-full h-full flex-col">
          {currentView === "initial" && (
            <div className="flex w-full items-center justify-center flex-col p-5 gap-5">
              <div className="flex flex-row gap-5">
                <ActionCard
                  icon={<LinkObject />}
                  text={t(`${TRANSLATION_KEY}.linkExistingField`)}
                  onClick={handleLinkExistingField}
                />
                <ActionCard
                  icon={<Add />}
                  text={t(`${TRANSLATION_KEY}.startFromScratch`)}
                  onClick={handleStartFromScratch}
                />
              </div>
              {entityType !== EntityType.PERSON &&
                entityType !== EntityType.LEAD &&
                entityType !== EntityType.CONTRACT && (
                  <AddPresetField
                    scoFields={scoFields}
                    onPresetFieldEdit={handlePresetFieldEdit}
                  />
                )}
            </div>
          )}

          {currentView === "linkedField" && (
            <AddLinkedField
              onBack={handleBack}
              onCancel={handleClose}
              onSave={handleSave}
              entityType={entityType}
            />
          )}

          {currentView === "customField" && (
            <AddCustomField
              onBack={handleBack}
              onCancel={handleClose}
              onSave={handleSave}
              entityType={entityType}
            />
          )}

          {currentView === "presetField" && selectedPresetField && (
            <AddPresetFieldEdit
              presetField={selectedPresetField}
              onBack={handleBack}
              onCancel={handleClose}
              onSave={handleSave}
            />
          )}
        </div>
      </AppDrawerContent>
    </Drawer>
  );
};
